import 'babel-polyfill';
import React, { Component, useEffect } from 'react';
import './App.css';
import PropTypes from 'prop-types';
import {
  withStyles,
  createMuiTheme,
  ThemeProvider,
} from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import DownloadDialog from './components/DownloadDialog.js';
import * as d3 from 'd3';
import EnergySourceSelector from './components/energySourceSelector';
import FilterSelector from './components/filterSelector';
import DateSelector from './components/dateSelector';
import EnergyMapFlanders from './containers/EnergyMapFlanders';
import VideoControl from './containers/VideoControl';
import ProductionTitle from './components/productionTitle';
import DayTopIndicatorCard from './components/dayTopIndicatorCard';
import HourCard from './components/hourCard';
import TimePlot from './containers/TimePlot';
import BulletChart from './containers/BulletChart';
import SocialMedia from './components/socialMedia';
import StatisticsOverviewCard from './components/statisticsOverviewCard';
import ProvinceBoxCard from './components/provinceBoxCard';
import CalendarBlockCard from './components/calendarBlockCard';
import Faq from './components/faq.js';
import Stats from './components/stats.js';
import Copyright from './components/copyright';
import VeaBottomNavigation from './components/veaBottomNavigaton';
import CardMoreInfoPrices from './components/cardMoreInfoPrices.js';
import Promise from 'promise-polyfill';
import {
  solarColorArray,
  windColorArray,
  allColorArray,
} from './styles/colors.js';
import withMaintenanceMode from "./withMaintenanceMode";

// To add to window
if (!window.Promise) {
  window.Promise = Promise;
}

const breakpointTheme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      tablet: 768,
      md: 960,
      lg: 1280,
      lg1366: 1366,
      lg1440: 1440,
      lg1536: 1536,
      lg1660: 1660,
      xl: 1920,
    },
  },
  // not much of a palette has been defined yet so I'm reusing most common colors from the UI
  palette: {
    primary: {
      light: '#61dafb',
      main: '#165469',
      dark: '#39596a',
    },
    secondary: {
      light: '#61dafb',
      main: '#165469',
      dark: '#39596a',
    },
  },
});

const styles = (theme) => ({
  root: {
    padding: '10px',
    [theme.breakpoints.down('xs')]: {
      paddingBottom: '80px',
    },
  },
  media: {
    height: '100px',
    backgroundSize: 'contain',
  },
  spaceBetween: {
    justifyContent: 'space-between',
  },
  hooverable: {
    textDecoration: 'none',
    '&:hover': {
      color: 'white!important',
      cursor: 'pointer',
    },
  },
  appTitle: {
    fontSize: '20px',
    fontWeight: 700,
    textAlign: 'left',
  },
  latestUpdateContainer: {
    color: '#9e9e9e',
    verticalAlign: 'middle',
    lineHeight: '30px',
    marginRight: '10px',
    display: 'inline-block',
    [theme.breakpoints.up('md')]: {
      marginBottom: '30px',
    },
    [theme.breakpoints.up('lg1566')]: {
      marginBottom: '0px',
    },
  },
  socialMediaBlock: {
    position: 'absolute',
    width: '190px',
    right: '5px',
    top: 0,
  },
  bottomBoxes: {
    fontSize: '14pt',
    [theme.breakpoints.up('lg')]: {
      padding: '20px',
      fontSize: '13pt',
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: '14pt',
    },
  },
  bottomUpdateCopyrightBlock: {
    marginTop: '10px',
    [theme.breakpoints.up('sm')]: {
      marginBottom: '80px',
    },
  },
  statSiteLink: {
    fontFamily: 'Flanders Art Sans!important',
  },
  mapContainer: {
    padding: 12,
  },
  moreInfoPricesContainer: {
    [theme.breakpoints.up('sm')]: {
      order: 1,
    },
    [theme.breakpoints.up('md')]: {
      order: 0,
    },
  },
  downloadCard: {
    display: 'flex',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      minHeight: '70px',
      height: '70px',
    },
    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
    },
  },
  downloadCardButton: {
    display: 'flex',
    width: '100%',
    padding: 0,
    border: 0,
    fontSize: 'inherit',
    cursor: 'pointer',
  },
  downloadCardHeader: {
    fontFamily: 'Flanders Art Sans!important',
    backgroundColor: '#f3f3f3',
    color: '#fff',
    textAlign: 'left',
    height: '100%',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      padding: '0!important',
    },
  },
});

// manually parse a CSV price file
const parsePrices = (row) => {
  const value = parseFloat(row[0], 10);

  if (!isNaN(value)) {
    return {
      datetime: row.datetime,
      value,
    };
  }
};

let isIE = /*@cc_on!@*/ false || !!document.documentMode;

let initialPlayingState = isIE ? false : true;
let motionTimerInterval = isIE ? 2500 : 1250;

class App extends Component {
  constructor(props) {
    super(props);

    this.loadData = this.loadData.bind(this);
    this.updateDynamicData = this.updateDynamicData.bind(this);
    this.loadStaticData = this.loadStaticData.bind(this);
    this.updateStaticData = this.updateStaticData.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.setNewDate = this.setNewDate.bind(this);
    this.setNewYear = this.setNewYear.bind(this);
    this.toggleMode = this.toggleMode.bind(this);
    this.displayCalendar = this.displayCalendar.bind(this);
    this.updateComponents = this.updateComponents.bind(this);
    this.toggleDownload = this.toggleDownload.bind(this);
    //this.changeLanguage = this.changeLanguage.bind(this);
  }

  state = {
    dataLoaded: false,
    dataLoading: false,
    staticDataLoaded: false,
    componentsUpdated: false,
    daySelected: d3.timeFormat('%Y%m%d')(new Date()),
    // daySelected: '20200901',
    previousDaySelected: d3.timeFormat('%Y%m%d')(new Date()),
    // previousDaySelected: '20200831',
    yearSelected: new Date().getFullYear() - 1,
    //yearSelected: 2017,
    currentTech: {
      solar: true,
      wind: false,
      windOffshore: false,
    },
    previousTech: {
      solar: true,
      wind: false,
      windOffshore: false,
    },
    currentMode: 'realtime',
    currentFilter: 'energy',
    previousFilter: 'energy',
    data: {
      production: {
        solar: [],
        wind: [],
        windOffshore: [],
      },
      installed: {
        solar: [],
        wind: [],
        windOffshore: [],
      },
      demand: [],
      statistics: {},
      calendarStats: {},
      inhabitants: [],
    },
    staticData: {
      surfaces: [],
      vlaanderen: {},
      co2Avoidances: [],
    },
    formattedData: {
      times: [],
      techSums: [],
      bulletData: {
        sums: [],
        demand_sums: [],
        covered: [],
      },
      nisSums: [],
      offshoreSums: [],
      installedCapacities: [],
      installedCapacityOffshore: 1,
      dayProductions: [],
      dayProductionOffshore: 0,
      uncutDayProductions: [],
      hasForecast: false,
      plotIndicators: [],
      prices: [],
    },
    selectedDate: new Date(),
    // selectedDate: new Date('2020-09-01'),
    currentDateTime: '', //used for step management, set with times[i], also used in timeplot for checking forecast
    lastUpdateTime: '',
    colorArray: [],
    activeStep: 10,
    maxValue: 100,

    currentYearNavigationDisabled: false,
    showCalendar: false,
    calendarMode: 'realtime',

    playing: initialPlayingState,
    justToggled: true,
    screenSizeToggle: false,
    windowWidth: window.innerWidth,
    lang: 'nl',
    downloadIsVisible: false,
  };

  componentDidMount() {
    this.loadData();
    this.loadStaticData();
    this.updateTimer = setInterval(this.setLastUpdateTime(), 900000);
    window.addEventListener('resize', this.handleResize);

    if (process.env.NODE_ENV === 'production') {
      var _mtm = (window._mtm = window._mtm || []);
      _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });
      var d = document,
        g = d.createElement('script'),
        s = d.getElementsByTagName('script')[0];
      g.async = true;
      g.src = process.env.REACT_APP_MATOMO_BASE_URL;
      s.parentNode.insertBefore(g, s);
    }
  }
  // changeLanguage = (language) => {
  //   if (this.state.lang !== language) {
  //     this.setState({ lang: language })
  //   }
  // }
  loadData() {
    const daySelected = this.state.daySelected;
    let yearSelected = this.state.yearSelected;
    let currentYearNavigationDisabled = false;
    const currentYear = parseInt(d3.timeFormat('%Y')(new Date()));

    const currentMode = this.state.currentMode;

    const updateData = (data) => {
      this.updateDynamicData(data);
    };

    d3.csv('./data/historical/historical_solar_' + currentYear + '.csv')
      .then(function (data) {
        const currentYearData = data;
        if (currentYearData.length === 0) {
          console.log('currentYearData has length 0');
          currentYearNavigationDisabled = true;
        } else {
          const datetime = currentYearData[0]['datetime'];
          if (typeof datetime === 'undefined') {
            console.log('datetime is undefined');
            currentYearNavigationDisabled = true;
          } else {
            if (currentYearData.columns === 'undefined') {
              console.log('currentYearData columns are absent');
              currentYearNavigationDisabled = true;
            } else {
              console.log('currentYearData data is present');
            }
          }
        }
      })
      .catch(function (error) {
        console.log(
          'error occurred while trying to get file historical_solar_' +
            currentYear,
        );
        console.log(error);
        currentYearNavigationDisabled = true;
      })
      .then(function () {
        if (currentMode === 'realtime') {
          yearSelected = daySelected.substring(0, 4);
          return new Promise(function (resolve, reject) {
            Promise.all([
              d3.csv('./data/realtime/realtime_solar_' + daySelected + '.csv'),
              d3.csv('./data/realtime/realtime_wind_' + daySelected + '.csv'),
              d3.csv(
                './data/realtime/realtime_wind_offshore_' +
                  daySelected +
                  '.csv',
              ),
              d3.csv(
                './data/realtime/installed_capacity_solar_' +
                  daySelected +
                  '.csv',
              ),
              d3.csv(
                './data/realtime/installed_capacity_wind_' +
                  daySelected +
                  '.csv',
              ),
              d3.csv(
                './data/realtime/installed_capacity_wind_offshore_' +
                  daySelected +
                  '.csv',
              ),
              // https://stackoverflow.com/questions/13870265/read-csv-tsv-with-no-header-line-in-d3
              d3.text(
                './data/realtime/realtime_demand_' + daySelected + '.csv',
              ),
              d3.json('./data/realtime/overview_' + daySelected + '.json'),
              d3.json('./data/realtime/calendar_events_realtime.json'),
              d3.csv('./data/inhabitants/inhabitants_' + yearSelected + '.csv'),
              // fetching as a text to properly parse later. Somehow when the file doesn't exist (only published the day before from 13.00) the server returns an HTML document and not an error
              d3
                .text(`./data/realtime/realtime_price_${daySelected}.csv`)
                .then((d) => {
                  const parsed = d3.csvParse(d, parsePrices);
                  return parsed;
                })
                .catch((e) => {
                  console.error({ e });
                  return [];
                }),
            ])
              .then(function (d, error) {
                if (error) {
                  console.log(error);
                } else {
                  d[6] = d3.csvParseRows(d[6]);
                  d.push(currentYearNavigationDisabled);
                  return d;
                }
              })
              .then((resultData) => resolve(updateData(resultData)));
          });
        } else {
          return new Promise(function (resolve, reject) {
            Promise.all([
              d3.csv(
                './data/historical/historical_solar_' + yearSelected + '.csv',
              ),
              d3.csv(
                './data/historical/historical_wind_' + yearSelected + '.csv',
              ),
              d3.csv(
                './data/historical/historical_wind_offshore_' +
                  yearSelected +
                  '.csv',
              ),
              d3.csv(
                './data/historical/installed_capacity_solar_' +
                  yearSelected +
                  '.csv',
              ),
              d3.csv(
                './data/historical/installed_capacity_wind_' +
                  yearSelected +
                  '.csv',
              ),
              d3.csv(
                './data/historical/installed_capacity_wind_offshore_' +
                  yearSelected +
                  '.csv',
              ),
              // https://stackoverflow.com/questions/13870265/read-csv-tsv-with-no-header-line-in-d3
              d3.text(
                './data/historical/historical_load_' + yearSelected + '.csv',
              ),
              d3.json(
                './data/historical/historical_overview_' +
                  yearSelected +
                  '.json',
              ),
              d3.json(
                './data/historical/calendar_events_' + yearSelected + '.json',
              ),
              d3.csv('./data/inhabitants/inhabitants_' + yearSelected + '.csv'),
              // manually parsing afterwards
              d3
                .text(`./data/realtime/realtime_price_${daySelected}.csv`)
                .then((d) => {
                  const parsed = d3.csvParse(d, parsePrices);
                  console.log({ d, parsed });
                  return parsed;
                })
                .catch((e) => {
                  console.error({ e });
                  return [];
                }),
            ])
              .then(function (d, error) {
                if (error) {
                  console.log(error);
                } else {
                  d[6] = d3.csvParseRows(d[6]);
                  d.push(currentYearNavigationDisabled);
                  return d;
                }
              })
              .then((resultData) => resolve(updateData(resultData)));
          });
        }
      });
  }

  updateDynamicData(resultData) {
    this.setState((previousState, props) => {
      return {
        data: {
          ...previousState.data,
          production: {
            solar: resultData[0],
            wind: resultData[1],
            windOffshore: resultData[2],
          },
          installed: {
            solar: resultData[3],
            wind: resultData[4],
            windOffshore: resultData[5],
          },
          demand: resultData[6],
          statistics: resultData[7],
          calendarStats: resultData[8],
          inhabitants: resultData[9],
          prices: resultData[10],
        },
        dataLoading: true,
        dataLoaded: true,
        currentYearNavigationDisabled: resultData[resultData.length - 1],
      };
    });
  }

  loadStaticData() {
    const updateData = (data) => {
      this.updateStaticData(data);
    };
    return new Promise(function (resolve, reject) {
      Promise.all([
        d3.csv('./map_data/surfaces.csv'),
        d3.json('./map_data/static_data_vlaanderen.json'),
        d3.csv('./data/yearly_evolution_ton_co2_per_gwh.csv'),
      ]).then(function (d, error) {
        if (error) {
          console.log(error);
        } else {
          resolve(updateData(d));
        }
      });
    });
  }

  updateStaticData(resultData) {
    this.setState((previousState, props) => {
      return {
        staticData: {
          ...previousState.staticData,
          surfaces: resultData[0],
          vlaanderen: resultData[1],
          co2Avoidances: resultData[2],
        },
        staticDataLoaded: true,
      };
    });
  }

  handleResize() {
    this.setState({
      windowWidth: window.innerWidth,
    });
  }

  changeCurrentDaySelected = (selectedDate) => {
    if (this.state.daySelected !== selectedDate) {
      clearInterval(this.motionTimer);

      const newDate = new Date(
        selectedDate.substring(0, 4) +
          '/' +
          selectedDate.substring(4, 6) +
          '/' +
          selectedDate.substring(6, 8),
      );
      this.setState((previousState, props) => {
        let updatedStep = parseInt(d3.timeFormat('%H')(new Date()));
        if (previousState.playing) {
          if (
            previousState.currentTech.solar &&
            !previousState.currentTech.wind
          )
            updatedStep = 6;
          else updatedStep = 0;
        }

        return {
          daySelected: selectedDate,
          selectedDate: newDate,
          componentsUpdated: false,
          dataLoaded: false,
          dataLoading: false,
          showCalendar: false,

          activeStep: updatedStep,
          justToggled: true,
        };
      });
    }
  };

  changeCurrentYearSelected = (e) => {
    const selectedYear = parseInt(e.currentTarget.dataset.year_selected);
    if (this.state.yearSelected !== selectedYear) {
      // 20181115 ensure that for each new year the timer gets reset to ensure the correct number of max steps
      clearInterval(this.motionTimer);

      this.setState({
        yearSelected: selectedYear,
        componentsUpdated: false,
        dataLoaded: false,
        dataLoading: false,
        showCalendar: false,

        activeStep: 0,
        justToggled: true,
      });
    }
  };

  setFilter = (e) => {
    const target = e.currentTarget;
    this.setState({
      currentFilter: target.dataset.filter,
    });
  };

  setNewDate = (selectedDate) => {
    const newDate = new Date(selectedDate);
    const daySelected = d3.timeFormat('%Y%m%d')(newDate);
    if (daySelected === this.state.daySelected) {
      if (this.state.currentMode === 'realtime') {
        this.displayCalendar(false);
      } else {
        this.toggleMode();
      }
    } else {
      // 20181116 ensure that for each new day the timer gets reset to ensure the correct number of max steps
      clearInterval(this.motionTimer);

      this.setState((previousState, props) => {
        let updatedStep = parseInt(d3.timeFormat('%H')(new Date()));
        if (previousState.playing) {
          if (
            previousState.currentTech.solar &&
            !previousState.currentTech.wind
          )
            updatedStep = 6;
          else updatedStep = 0;
        }

        return {
          daySelected: daySelected,
          selectedDate: newDate,
          componentsUpdated: false,
          dataLoaded: false,
          dataLoading: false,
          showCalendar: false,

          currentMode: 'realtime',
          activeStep: updatedStep,

          justToggled: true,
        };
      });
    }
  };

  setNewYear = (e) => {
    //const newYear = new Date(selectedDate)
    //const daySelected = d3.timeFormat("%Y%m%d")(newDate);
    const selectedYear = parseInt(e.currentTarget.textContent);
    if (selectedYear === this.state.yearSelected) {
      if (this.state.currentMode === 'realtime') {
        this.toggleMode();
      } else {
        this.displayCalendar(false);
      }
    } else {
      // 20181116 ensure that for each new day the timer gets reset to ensure the correct number of max steps
      clearInterval(this.motionTimer);

      this.setState((previousState, props) => {
        return {
          yearSelected: selectedYear,
          //selectedDate: newDate,
          componentsUpdated: false,
          dataLoaded: false,
          dataLoading: false,
          showCalendar: false,
          currentMode: 'historical',
          activeStep: 0,

          justToggled: true,
        };
      });
    }
  };

  toggleMode() {
    // 20181115 ensure that when changing mode the timer gets reset to ensure the correct number of max steps
    clearInterval(this.motionTimer);

    this.setState((previousState, props) => {
      let updatedMode;
      let updatedStep;

      if (previousState.currentMode === 'realtime') {
        updatedMode = 'historical';
        updatedStep = 0;
      } else {
        updatedMode = 'realtime';
        updatedStep = parseInt(d3.timeFormat('%H')(new Date()));
      }
      return {
        currentMode: updatedMode,
        componentsUpdated: false,
        dataLoaded: false,
        dataLoading: false,
        showCalendar: false,
        activeStep: updatedStep,
        // added this so that we can set the timer correctly again after changing mode
        justToggled: true,
      };
    });

    // 20181115 we cannot call setNewPlayingState to set the timer up now as we do not have the data yet; instead this will be done in updateComponents at the very end
  }

  displayCalendar = (boolShowCalendar, calendarMode) => {
    this.setState({
      showCalendar: boolShowCalendar,
      calendarMode: calendarMode,
    });
  };

  setSelectedEnergySource = (e) => {
    const target = e.target;
    this.setState((previousState, props) => {
      let newTech = { ...previousState.currentTech };

      switch (target.dataset.energy_source) {
        case 'solar':
          if (!newTech.solar || newTech.wind || newTech.windOffshore) {
            newTech.solar = !newTech.solar;
          } else {
            // do nothing or show an alert
          }
          break;
        case 'wind':
          if (newTech.solar || !newTech.wind || newTech.windOffshore) {
            newTech.wind = !newTech.wind;
          } else {
            // do nothing or show an alert
          }
          break;
        case 'windOffshore':
          if (newTech.solar || newTech.wind || !newTech.windOffshore) {
            newTech.windOffshore = !newTech.windOffshore;
          } else {
            // do nothing or show an alert
          }
          break;
        default:
      }
      return {
        currentTech: newTech,
        componentsUpdated: false,
        currentFilter: 'energy',
      };
    });
  };

  setPlayingState = (newPlayingState) => {
    const maxSteps = this.state.formattedData.times.length;
    if (newPlayingState) {
      this.setState((previousState, props) => {
        if (
          previousState.currentTech.solar &&
          !previousState.currentTech.wind &&
          previousState.currentMode === 'realtime'
        )
          return { activeStep: 6, playing: true };
        else return { activeStep: 0, playing: true };
      });
      this.motionTimer = setInterval(
        () => this.changeActiveStep((this.state.activeStep + 1) % maxSteps),
        motionTimerInterval,
      );
    } else {
      clearInterval(this.motionTimer);
      this.setState({ playing: false });
    }
  };

  setLastUpdateTime() {
    let lastUpdateTimeHour = d3.timeFormat('%Hu')(new Date());
    this.setState({ lastUpdateTime: lastUpdateTimeHour });
  }

  setActiveStep = (e) => {
    this.changeActiveStep(parseInt(e.currentTarget.dataset.active_step));
  };

  changeActiveStep = (newActiveStep) => {
    let newCurrentDateTime = this.state.currentDateTime;
    if (this.state.formattedData.times.length > 0)
      newCurrentDateTime = this.state.formattedData.times[newActiveStep];

    let installedCapacities = this.state.formattedData.installedCapacities;
    if (this.state.currentMode === 'historical') {
      // 20190403 installedCapacities is used for map only to display tooltip, so we do not take the offshore into account
      if (this.state.currentTech.solar) {
        if (this.state.currentTech.wind)
          installedCapacities = this.sumObjectsByKey(
            this.state.data.installed.solar[newActiveStep],
            this.state.data.installed.wind[newActiveStep],
          );
        else
          installedCapacities = this.state.data.installed.solar[newActiveStep];
      } else
        installedCapacities = this.state.data.installed.wind[newActiveStep];
    }
    this.setState((previousState, props) => {
      return {
        activeStep: newActiveStep,
        currentDateTime: newCurrentDateTime,
        formattedData: {
          ...previousState.formattedData,
          installedCapacities: installedCapacities,
        },
      };
    });
  };

  componentDidUpdate() {
    if (this.state.dataLoaded) {
      let stateupdated =
        JSON.stringify(this.state.currentTech) ===
          JSON.stringify(this.state.previousTech) &&
        this.state.currentFilter === this.state.previousFilter &&
        this.state.daySelected === this.state.previousDaySelected;
      if (!stateupdated || !this.state.componentsUpdated)
        this.updateComponents();
    } else {
      if (!this.state.dataLoading) {
        this.loadData();
      }
    }
    //}
  }

  sumObjectsByKey(...objs) {
    return objs.reduce((a, b) => {
      for (let k in b) {
        if (Object.prototype.hasOwnProperty.call(b, k))
          a[k] = (parseFloat(a[k]) || 0) + parseFloat(b[k]);
      }
      return a;
    }, {});
  }

  updateComponents() {
    let techs = [];
    const currentTech = this.state.currentTech;
    const currentFilter = this.state.currentFilter;
    const currentMode = this.state.currentMode;

    let techData = this.state.data;
    let staticData = this.state.staticData;
    let inhabitants = d3
      .nest()
      .key(function (k) {
        return k.nis;
      })
      .rollup(function (v) {
        return v[0].value;
      })
      .object(techData.inhabitants);
    let surfaces = d3
      .nest()
      .key(function (k) {
        return k.nis;
      })
      .rollup(function (v) {
        return v[0].value / 1000000;
      })
      .object(staticData.surfaces);

    let colorArray = [];

    let installedCapacityOffshore = 0;
    let installedCapacities;

    if (currentTech.windOffshore) {
      techs.push('windOffshore');
      installedCapacityOffshore = techData.installed.windOffshore[0][0];
    }
    if (!currentTech.solar) {
      colorArray = windColorArray;
      if (currentTech.wind) {
        techs.push('wind');
        if (this.state.currentMode === 'realtime')
          installedCapacities = techData.installed.wind[0];
        else
          installedCapacities = techData.installed.wind[this.state.activeStep];
      } else {
        // to not break the code, but installedCapacities is only relevant for the EnergyMapFlanders
        installedCapacities = techData.installed.windOffshore[0];
      }
    } else {
      if (currentTech.wind) {
        techs.push('wind');
        colorArray = allColorArray;
        if (this.state.currentMode === 'realtime')
          installedCapacities = this.sumObjectsByKey(
            techData.installed.solar[0],
            techData.installed.wind[0],
          );
        else {
          installedCapacities = this.sumObjectsByKey(
            techData.installed.solar[this.state.activeStep],
            techData.installed.wind[this.state.activeStep],
          );
        }
      } else {
        if (currentTech.windOffshore) {
          colorArray = allColorArray;
        } else {
          colorArray = solarColorArray;
        }
        if (this.state.currentMode === 'realtime')
          installedCapacities = techData.installed.solar[0];
        else
          installedCapacities = techData.installed.solar[this.state.activeStep];
      }
      techs.push('solar');
    }

    let d = [];
    techs.map(function (tech, i) {
      if (techData.production[tech].length > 0) {
        techData.production[tech].map(function (r) {
          let rowDateTime = r.datetime;
          Object.keys(r)
            .filter((key) => key !== 'datetime')
            .map((nis) => {
              let v = {
                tech: tech,
                datetime: rowDateTime,
                nis: nis,
                value: parseFloat(r[nis]),
              };
              d.push(v);
              return true;
            });
          return true;
        });
      }
      return true;
    });
    d = [].concat.apply([], d);

    let times = d3
      .set(d, function (k) {
        return k.datetime;
      })
      .values()
      .map(function (k) {
        const kYear = k.substring(0, 4);
        const kMonth = parseInt(k.substring(5, 7)) - 1;
        const kDay = k.substring(8, 10);
        const kHour = k.substring(11, 13);
        const kMinute = k.substring(14, 16);
        const kSecond = k.substring(17, 19);
        const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
        return kDate;
      });
    let techSums = d3
      .nest()
      .key(function (k) {
        return k.tech;
      })
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value / 1000;
        });
      })
      .object(d);

    let plotIndicatorObjects = d3
      .nest()
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value;
        });
      })
      .object(
        d.filter(function (v) {
          return v.tech !== 'demand';
        }),
      );

    let plotIndicators = Object.keys(plotIndicatorObjects).map(function (k) {
      return {
        datetime: new Date(k),
        value: plotIndicatorObjects[k],
      };
    });
    let totInh = 0;
    if (currentFilter === 'inhabitants') {
      Object.keys(inhabitants).map((key) => {
        totInh += parseFloat(inhabitants[key]);
        return true;
      });
    }
    techSums = Object.keys(techSums)
      .filter(function (t) {
        return t !== 'demand';
      })
      .map(function (k) {
        return {
          name: k,
          values: Object.keys(techSums[k]).map(function (v) {
            let val = techSums[k][v];
            if (currentFilter === 'surface') {
              if (currentMode === 'realtime')
                val =
                  (val /
                    d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
                  100000;
              else
                val =
                  (val /
                    d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
                  100;
            } else if (currentFilter === 'inhabitants') {
              val = (1000000000 * val) / totInh;
            }
            const kYear = v.substring(0, 4);
            const kMonth = parseInt(v.substring(5, 7)) - 1;
            const kDay = v.substring(8, 10);
            const kHour = v.substring(11, 13);
            const kMinute = v.substring(14, 16);
            const kSecond = v.substring(17, 19);
            const kDate = new Date(
              kYear,
              kMonth,
              kDay,
              kHour,
              kMinute,
              kSecond,
            );
            return {
              datetime: kDate,
              value: val,
            };
          }),
        };
      });
    //for map: OFFSHORE EXCLUDED

    let nisSums = d3
      .nest()
      .key(function (k) {
        return k.datetime;
      })
      .key(function (k) {
        return k.nis;
      })
      .rollup(function (v, k) {
        let val = d3.sum(
          v.filter(function (f) {
            return f.tech !== 'demand' && f.tech !== 'windOffshore';
          }),
          function (e) {
            return e.value;
          },
        );
        // DO NOT take windOffshore into account, but later take the max value from both series so that the returned maxValue is adequate!
        ////let val = d3.sum(v.filter(function (f) { return f.tech !== 'demand' }), function (e) { return e.value; });
        if (currentFilter === 'surface') {
          val = (val / parseFloat(surfaces[v[0].nis])) * 100;
        } else if (currentFilter === 'inhabitants') {
          // 20181007 OLD INHABITANTS CSV HAD STRUCTURE NIS-YEAR-VALUE THIS IS NOW ID-YEAR
          //val = val / parseFloat(Object.values(inhabitants[v[0].nis])[Object.values(inhabitants[v[0].nis]).length - 1]) * 100000;
          if (val > 0) {
            val = (val / parseFloat(inhabitants[v[0].nis])) * 100000;
          }
        }
        return val;
      })
      .object(d);

    nisSums = Object.keys(nisSums).map(function (k) {
      const kYear = k.substring(0, 4);
      const kMonth = parseInt(k.substring(5, 7)) - 1;
      const kDay = k.substring(8, 10);
      const kHour = k.substring(11, 13);
      const kMinute = k.substring(14, 16);
      const kSecond = k.substring(17, 19);
      const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
      return {
        datetime: kDate,
        values: nisSums[k],
      };
    });

    // calculus max comes from map js where it was used in the legend
    let maxValue = d3.max(nisSums, function (t) {
      // https://stackoverflow.com/questions/38748445/
      //return d3.max(Object.values(t.values))
      return d3.max(Object.keys(t.values).map((key) => t.values[key]));
    });

    let dayProductions = d3
      .nest()
      .key(function (k) {
        return k.nis;
      })
      .rollup(function (v) {
        let val = d3.sum(
          v.filter(function (f) {
            return f.tech !== 'demand' && f.tech !== 'windOffshore';
          }),
          function (e) {
            return e.value;
          },
        );
        return val;
      })
      .object(d);
    let uncutDayProductions = dayProductions;
    if (currentFilter !== 'energy') {
      dayProductions = d3
        .nest()
        .key(function (k) {
          return k.nis;
        })
        .rollup(function (v) {
          let val = d3.sum(
            v.filter(function (f) {
              return f.tech !== 'demand' && f.tech !== 'windOffshore';
            }),
            function (e) {
              return e.value;
            },
          );
          if (currentFilter === 'surface') {
            val = (val / parseFloat(surfaces[v[0].nis])) * 100;
          } else if (currentFilter === 'inhabitants') {
            if (val > 0) {
              val = (val / parseFloat(inhabitants[v[0].nis])) * 100000;
            }
          }
          return val;
        })
        .object(d);
    }

    //for map: OFFSHORE DATA
    let offshoreSums = this.state.offshoreSums;
    let dayProductionOffshore = this.state.dayProductionOffshore;
    if (this.state.currentTech.windOffshore) {
      offshoreSums = d3
        .nest()
        .key(function (k) {
          return k.datetime;
        })
        .key(function (k) {
          return k.nis;
        })
        .rollup(function (v, k) {
          let val = d3.sum(
            v.filter(function (f) {
              return f.tech === 'windOffshore';
            }),
            function (e) {
              return e.value;
            },
          );
          return val;
        })
        .object(d);

      offshoreSums = Object.keys(offshoreSums).map(function (k) {
        const kYear = k.substring(0, 4);
        const kMonth = parseInt(k.substring(5, 7)) - 1;
        const kDay = k.substring(8, 10);
        const kHour = k.substring(11, 13);
        const kMinute = k.substring(14, 16);
        const kSecond = k.substring(17, 19);
        const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
        return {
          datetime: kDate,
          values: offshoreSums[k][0],
        };
      });

      let maxValueOffshore = d3.max(offshoreSums, function (t) {
        return t.values;
      });

      if (maxValueOffshore > maxValue) {
        maxValue = maxValueOffshore;
      }

      dayProductionOffshore = d3
        .nest()
        .key(function (k) {
          return k.nis;
        })
        .rollup(function (v) {
          let val = d3.sum(
            v.filter(function (f) {
              return f.tech === 'windOffshore';
            }),
            function (e) {
              return e.value;
            },
          );
          return val;
        })
        .object(d);
      dayProductionOffshore = dayProductionOffshore[0];
    }

    let sums = d3
      .nest()
      .key(function (k) {
        return k.datetime;
      })
      .rollup(function (v) {
        return d3.sum(v, function (e) {
          return e.value;
        });
      })
      .object(
        d.filter(function (v) {
          return v.tech !== 'demand';
        }),
      );
    sums = Object.keys(sums).map(function (k) {
      let val = sums[k];
      if (currentFilter === 'surface') {
        if (currentMode === 'realtime')
          val =
            (val / d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
            100000;
        else
          val =
            (val / d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
            100;
      } else {
        if (currentFilter === 'inhabitants') {
          val = (1000000000 * val) / totInh;
        }
      }
      const kYear = k.substring(0, 4);
      const kMonth = parseInt(k.substring(5, 7)) - 1;
      const kDay = k.substring(8, 10);
      const kHour = k.substring(11, 13);
      const kMinute = k.substring(14, 16);
      const kSecond = k.substring(17, 19);
      //const kOffSetHours=v.substring(21,22)
      const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
      //kDate.setHours(kDate.getHours() - kOffSetHours);
      return {
        datetime: kDate,
        value: val,
      };
    });
    let demand = this.state.data.demand;
    let demand_sums;

    if (demand) {
      demand_sums = d3
        .nest()
        .key(function (k) {
          return k[0];
        })
        .rollup(function (v) {
          if (v.length > 0) return parseFloat(v[0][1]);
          else return 0; // added because v turned out empty
        })
        .object(demand);

      demand_sums = Object.keys(demand_sums).map(function (k) {
        let val = demand_sums[k];
        if (currentFilter === 'surface') {
          if (currentMode === 'realtime')
            val =
              (val /
                d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
              100000;
          else
            val =
              (val /
                d3.sum(Object.keys(surfaces).map((key) => surfaces[key]))) *
              100;
        } else {
          if (currentFilter === 'inhabitants') {
            val = (1000000000 * val) / totInh;
          }
        }
        const kYear = k.substring(0, 4);
        const kMonth = parseInt(k.substring(5, 7)) - 1;
        const kDay = k.substring(8, 10);
        const kHour = k.substring(11, 13);
        const kMinute = k.substring(14, 16);
        const kSecond = k.substring(17, 19);
        const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
        return {
          datetime: kDate,
          value: val,
        };
      });
    }

    let covered = Object.keys(sums).map(function (k) {
      const kYear = k.substring(0, 4);
      const kMonth = parseInt(k.substring(5, 7)) - 1;
      const kDay = k.substring(8, 10);
      const kHour = k.substring(11, 13);
      const kMinute = k.substring(14, 16);
      const kSecond = k.substring(17, 19);
      const kDate = new Date(kYear, kMonth, kDay, kHour, kMinute, kSecond);
      return {
        datetime: kDate,
        value: sums[k] / demand_sums[k],
      };
    });
    let bulletData = {
      sums: sums,
      demand_sums: demand_sums,
      covered: covered,
    };

    const setTimer = this.state.justToggled && this.state.playing;

    this.setState((previousState, props) => {
      let activeStep = previousState.activeStep;
      if (previousState.justToggled && previousState.playing) {
        if (
          previousState.currentTech.solar &&
          !previousState.currentTech.wind &&
          !previousState.currentTech.windOffshore &&
          previousState.currentMode === 'realtime'
        )
          activeStep = 6;
        else activeStep = 0;
      }
      return {
        formattedData: {
          ...previousState.formattedData,
          times: times,
          techSums: techSums,
          bulletData: bulletData,
          nisSums: nisSums,
          offshoreSums: offshoreSums,
          installedCapacities: installedCapacities,
          installedCapacityOffshore: installedCapacityOffshore,
          dayProductions: dayProductions,
          dayProductionOffshore: dayProductionOffshore,
          uncutDayProductions: uncutDayProductions,
          plotIndicators: plotIndicators,
          prices: this.state.data.prices,
        },
        componentsUpdated: true,
        colorArray: colorArray,
        maxValue: maxValue,
        previousDaySelected: previousState.daySelected,
        previousTech: previousState.currentTech,
        previousFilter: previousState.currentFilter,
        currentDateTime: times[activeStep],
        activeStep: activeStep,
        justToggled: false,
      };
    });
    if (setTimer) {
      const maxSteps = times.length;
      this.motionTimer = setInterval(
        () => this.changeActiveStep((this.state.activeStep + 1) % maxSteps),
        1250,
      );
    }
  }
  toggleDownload() {
    this.setState({
      isDownloadVisible: !this.state.isDownloadVisible,
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    clearInterval(this.motionTimer);
    clearInterval(this.updateTimer);
  }

  render() {
    const { classes } = this.props;

    let statisticsData = this.state.data.statistics;
    let statisticsDataWithoutOffshore;

    if (this.state.currentTech.solar) {
      if (this.state.currentTech.wind) {
        statisticsDataWithoutOffshore = statisticsData['combined'];
        if (this.state.currentTech.windOffshore) {
          statisticsData = statisticsData['all'];
        } else {
          statisticsData = statisticsData['combined'];
        }
      } else {
        statisticsDataWithoutOffshore = statisticsData['solar'];
        if (this.state.currentTech.windOffshore) {
          statisticsData = statisticsData['solar_offshore'];
        } else {
          statisticsData = statisticsData['solar'];
        }
      }
    } else {
      if (this.state.currentTech.wind) {
        statisticsDataWithoutOffshore = statisticsData['wind'];
        if (this.state.currentTech.windOffshore) {
          statisticsData = statisticsData['wind_wind'];
        } else {
          statisticsData = statisticsData['wind'];
        }
      } else {
        statisticsDataWithoutOffshore = statisticsData['wind_offshore'];
        statisticsData = statisticsData['wind_offshore'];
      }
    }

    const comparisonTime = new Date(this.state.currentDateTime).getTime();

    let currentPlotData = [];
    if (this.state.formattedData.plotIndicators.length > 0) {
      currentPlotData = this.state.formattedData.plotIndicators.filter(
        function (v) {
          return v.datetime.getTime() === comparisonTime;
        },
      );
    }
    let currentPlotValue = 0;
    if (currentPlotData.length > 0)
      currentPlotValue = Math.round(currentPlotData[0].value / 100) / 10;

    let latestUpdate;
    switch (this.state.lang) {
      case 'de':
        latestUpdate = 'Laatste update: ';
        break;
      case 'en':
        latestUpdate = 'Latest update: ';
        break;
      case 'fr':
        latestUpdate = 'Dernière mise à jour: ';
        break;
      default:
      case 'nl':
        latestUpdate = 'Laatste update: ';
    }

    if (this.state.lastUpdateTime) latestUpdate += this.state.lastUpdateTime;

    const co2AvoidedFactor = this.state.staticDataLoaded
      ? this.state.currentMode === 'realtime'
        ? parseInt(
            this.state.staticData.co2Avoidances.filter(
              (obj) => obj.year === this.state.daySelected.substring(0, 4),
            )[0]['ton/GWh'],
          )
        : parseInt(
            this.state.staticData.co2Avoidances.filter(
              (obj) => obj.year === this.state.yearSelected.toString(),
            )[0]['ton/GWh'],
          )
      : 1;
    return (
      <ThemeProvider theme={breakpointTheme}>
        <div className={'App ' + classes.root}>
          <div style={{ padding: '10px' }}>
            <Grid container spacing={this.state.windowWidth >= 1280 ? 5 : 2}>
              {this.state.windowWidth >= 768 && (
                <Grid item container xs={12} md={9} spacing={2}>
                  <Grid item container xs={12} sm={4} md={4}>
                    <EnergySourceSelector
                      currentTech={this.state.currentTech}
                      setEnergySource={this.setSelectedEnergySource}
                      dataLoaded={this.state.dataLoaded}
                      lang={this.state.lang}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5} md={5}>
                    <DateSelector
                      selectedDate={this.state.selectedDate}
                      selectedYear={this.state.yearSelected}
                      currentMode={this.state.currentMode}
                      changeCurrentDaySelected={this.changeCurrentDaySelected}
                      changeCurrentYearSelected={this.changeCurrentYearSelected}
                      lang={this.state.lang}
                      showCalendar={this.state.showCalendar}
                      displayCalendar={this.displayCalendar}
                      calendarMode={this.state.calendarMode}
                      onDateChange={this.setNewDate}
                      onYearChange={this.setNewYear}
                      onCloseClick={this.displayCalendar}
                      changeMode={this.toggleMode}
                      currentYearNavigationDisabled={
                        this.state.currentYearNavigationDisabled
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <FilterSelector
                      currentTech={this.state.currentTech}
                      currentFilter={this.state.currentFilter}
                      currentMode={this.state.currentMode}
                      setFilter={this.setFilter}
                      lang={this.state.lang}
                    />
                  </Grid>
                  <Grid item container xs={12} className={classes.mapContainer}>
                    <EnergyMapFlanders
                      currentTech={this.state.currentTech}
                      currentFilter={this.state.currentFilter}
                      currentMode={this.state.currentMode}
                      data={this.state.formattedData.nisSums}
                      maxValue={this.state.maxValue}
                      dayProductions={this.state.formattedData.dayProductions}
                      uncutDayProductions={
                        this.state.formattedData.uncutDayProductions
                      }
                      capacities={this.state.formattedData.installedCapacities}
                      co2AvoidedFactor={co2AvoidedFactor}
                      currentDateTime={this.state.currentDateTime}
                      dataReady={
                        this.state.dataLoaded &&
                        this.state.staticDataLoaded &&
                        this.state.componentsUpdated
                      }
                      vlaanderen={this.state.staticData.vlaanderen}
                      colorArray={this.state.colorArray}
                      yearSelected={this.state.yearSelected}
                      isPlaying={this.state.playing}
                      Width={this.state.windowWidth}
                      lang={this.state.lang}
                      showOffshoreIcon={this.state.currentTech.windOffshore}
                      offshoreData={this.state.formattedData.offshoreSums}
                      offshoreMaxValue={this.state.maxValue}
                      offshoreCapacity={
                        this.state.formattedData.installedCapacityOffshore
                      }
                      offshoreDayProduction={
                        this.state.formattedData.dayProductionOffshore
                      }
                    />
                    <Grid item xs={12} style={{ maxHeight: '120px' }}>
                      <VideoControl
                        activeStep={this.state.activeStep}
                        playing={this.state.playing}
                        data={this.state.formattedData.times}
                        dataReady={
                          this.state.dataLoaded && this.state.componentsUpdated
                        }
                        setPlayingState={this.setPlayingState}
                        changeActiveStep={this.changeActiveStep}
                        activeColor={
                          this.state.colorArray.length > 0
                            ? this.state.colorArray[5]
                            : '#3f51b5'
                        }
                        currentMode={this.state.currentMode}
                        lang={this.state.lang}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {this.state.windowWidth >= 768 && (
                <Grid
                  item
                  container
                  xs={12}
                  md={3}
                  className={'noSidePadding'}
                  spacing={0}
                  direction="column"
                  wrap="nowrap"
                >
                  <Grid item xs={12} style={{ flexBasis: 0 }}>
                    <ProductionTitle lang={this.state.lang} />
                  </Grid>
                  <Grid item xs={12} style={{ flexBasis: 0 }}>
                    <DayTopIndicatorCard
                      selectedDate={this.state.selectedDate}
                      selectedYear={this.state.yearSelected}
                      statisticsData={statisticsData}
                      currentTech={this.state.currentTech}
                      currentMode={this.state.currentMode}
                      lang={this.state.lang}
                    />
                  </Grid>

                  <Grid item xs={12} style={{ flexBasis: 0 }}>
                    <HourCard
                      activeStep={this.state.activeStep}
                      lang={this.state.lang}
                      currentMode={this.state.currentMode}
                    />
                  </Grid>
                  <Grid
                    item
                    container
                    className="bglightgrey"
                    xs={12}
                    style={{ flexBasis: 0 }}
                  >
                    <Grid item sm={6} md={12}>
                      <TimePlot
                        currentTech={this.state.currentTech}
                        currentFilter={this.state.currentFilter}
                        currentMode={this.state.currentMode}
                        data={this.state.formattedData.techSums}
                        prices={this.state.formattedData.prices}
                        currentDateTime={this.state.currentDateTime}
                        dataReady={
                          this.state.dataLoaded && this.state.componentsUpdated
                        }
                        trackerValue={currentPlotValue}
                        currentPlotValue={currentPlotValue}
                        windowWidth={this.state.windowWidth}
                        lang={this.state.lang}
                      />
                    </Grid>
                    <Grid
                      item
                      sm={12}
                      className={classes.moreInfoPricesContainer}
                    >
                      <CardMoreInfoPrices></CardMoreInfoPrices>
                    </Grid>
                    <Grid item sm={6} md={12}>
                      <BulletChart
                        currentTech={this.state.currentTech}
                        currentFilter={this.state.currentFilter}
                        currentMode={this.state.currentMode}
                        data={this.state.formattedData.bulletData}
                        currentDateTime={this.state.currentDateTime}
                        dataReady={
                          this.state.dataLoaded && this.state.componentsUpdated
                        }
                        activeStep={this.state.activeStep}
                        lang={this.state.lang}
                        windowWidth={this.state.windowWidth}
                      />
                    </Grid>
                  </Grid>
                  {this.state.windowWidth >= 960 && (
                    <div
                      className="bglightgrey"
                      style={{
                        position: 'relative',
                        width: '100%',
                        textAlign: 'left',
                        minHeight: '30px',
                      }}
                    >
                      <div className={classes.socialMediaBlock}>
                        <SocialMedia
                          source="timeplot"
                          currentTech={this.state.currentTech}
                          currentFilter={this.state.currentFilter}
                          currentMode={this.state.currentMode}
                          yearSelected={this.state.yearSelected}
                          daySelected={this.state.daySelected}
                        />
                      </div>
                    </div>
                  )}
                  {this.state.windowWidth < 960 && (
                    <div style={{ position: 'relative', width: '100%' }}>
                      <div
                        style={{
                          position: 'absolute',
                          right: '10px',
                          width: '200px',
                        }}
                      >
                        <SocialMedia
                          source="timeplot"
                          currentTech={this.state.currentTech}
                          currentFilter={this.state.currentFilter}
                          currentMode={this.state.currentMode}
                          yearSelected={this.state.yearSelected}
                          daySelected={this.state.daySelected}
                        />
                      </div>
                    </div>
                  )}
                </Grid>
              )}
              {this.state.windowWidth < 768 && (
                <Grid item container xs={12} className={'noSidePadding'}>
                  <Grid item xs={12} style={{ flexBasis: '0!important' }}>
                    <ProductionTitle lang={this.state.lang} />
                    <div style={{ textAlign: 'left' }}>
                      Dit is een vereenvoudigde versie van de stroomvoorspeller.
                      Op PC en tablet krijgt u nog veel meer informatie en
                      kaarten te zien.
                    </div>
                  </Grid>
                  <Grid item xs={12} style={{ flexBasis: '0!important' }}>
                    <DayTopIndicatorCard
                      selectedDate={this.state.selectedDate}
                      selectedYear={this.state.yearSelected}
                      statisticsData={statisticsData}
                      currentTech={this.state.currentTech}
                      currentMode={this.state.currentMode}
                      lang={this.state.lang}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <HourCard
                      activeStep={this.state.activeStep}
                      lang={this.state.lang}
                      currentMode={this.state.currentMode}
                    />
                  </Grid>
                  <Grid item container className="bglightgrey" xs={12}>
                    <Grid item xs={12}>
                      <TimePlot
                        currentTech={this.state.currentTech}
                        currentFilter={this.state.currentFilter}
                        currentMode={this.state.currentMode}
                        data={this.state.formattedData.techSums}
                        prices={this.state.formattedData.prices}
                        currentDateTime={this.state.currentDateTime}
                        dataReady={
                          this.state.dataLoaded && this.state.componentsUpdated
                        }
                        trackerValue={currentPlotValue}
                        currentPlotValue={currentPlotValue}
                        windowWidth={this.state.windowWidth}
                        lang={this.state.lang}
                      />
                      <CardMoreInfoPrices></CardMoreInfoPrices>
                    </Grid>
                    <Grid item xs={12}>
                      <VideoControl
                        activeStep={this.state.activeStep}
                        playing={this.state.playing}
                        data={this.state.formattedData.times}
                        dataReady={
                          this.state.dataLoaded && this.state.componentsUpdated
                        }
                        setPlayingState={this.setPlayingState}
                        changeActiveStep={this.changeActiveStep}
                        activeColor={
                          this.state.colorArray.length > 0
                            ? this.state.colorArray[5]
                            : '#3f51b5'
                        }
                        currentMode={this.state.currentMode}
                        lang={this.state.lang}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <BulletChart
                        currentTech={this.state.currentTech}
                        currentFilter={this.state.currentFilter}
                        currentMode={this.state.currentMode}
                        data={this.state.formattedData.bulletData}
                        currentDateTime={this.state.currentDateTime}
                        dataReady={
                          this.state.dataLoaded && this.state.componentsUpdated
                        }
                        activeStep={this.state.activeStep}
                        lang={this.state.lang}
                        windowWidth={this.state.windowWidth}
                      />
                    </Grid>
                  </Grid>

                  {this.state.windowWidth > 768 && (
                    <div style={{ position: 'relative' }}>
                      <div
                        style={{
                          position: 'absolute',
                          right: '10px',
                          width: '200px',
                        }}
                      >
                        <SocialMedia
                          source="timeplot"
                          currentTech={this.state.currentTech}
                          currentFilter={this.state.currentFilter}
                          currentMode={this.state.currentMode}
                          yearSelected={this.state.yearSelected}
                          daySelected={this.state.daySelected}
                        />
                      </div>
                    </div>
                  )}
                  {this.state.windowWidth < 768 && (
                    <Grid item xs={12}>
                      <div style={{ position: 'relative', minHeight: '30px' }}>
                        <div
                          style={{
                            position: 'absolute',
                            right: '10px',
                            width: '200px',
                          }}
                        >
                          <SocialMedia
                            source="timeplot"
                            currentTech={this.state.currentTech}
                            currentFilter={this.state.currentFilter}
                            currentMode={this.state.currentMode}
                            yearSelected={this.state.yearSelected}
                            daySelected={this.state.daySelected}
                          />
                        </div>
                      </div>
                    </Grid>
                  )}
                  <Grid item xs={12} className={classes.xsOnly}>
                    <DateSelector
                      selectedDate={this.state.selectedDate}
                      selectedYear={this.state.yearSelected}
                      currentMode={this.state.currentMode}
                      changeCurrentDaySelected={this.changeCurrentDaySelected}
                      changeCurrentYearSelected={this.changeCurrentYearSelected}
                      lang={this.state.lang}
                      showCalendar={this.state.showCalendar}
                      displayCalendar={this.displayCalendar}
                      calendarMode={this.state.calendarMode}
                      onDateChange={this.setNewDate}
                      onYearChange={this.setNewYear}
                      onCloseClick={this.displayCalendar}
                      changeMode={this.toggleMode}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </div>
          <div className={classes.bottomBoxes}>
            <Grid container spacing={this.state.windowWidth >= 1280 ? 5 : 2}>
              <Grid item xs={12} lg={4}>
                <StatisticsOverviewCard
                  selectedDate={this.state.selectedDate}
                  daySelected={this.state.daySelected}
                  selectedYear={this.state.yearSelected}
                  lang={this.state.lang}
                  statisticsData={statisticsData}
                  currentTech={this.state.currentTech}
                  currentMode={this.state.currentMode}
                  yearSelected={this.state.yearSelected}
                  dataLoaded={this.state.dataLoaded}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <ProvinceBoxCard
                  selectedDate={this.state.selectedDate}
                  selectedYear={this.state.yearSelected}
                  lang={this.state.lang}
                  provinceData={statisticsData}
                  provinceDataWithoutOffshore={statisticsDataWithoutOffshore}
                  currentTech={this.state.currentTech}
                  currentMode={this.state.currentMode}
                  daySelected={this.state.daySelected}
                  yearSelected={this.state.yearSelected}
                  dataLoaded={this.state.dataLoaded}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <CalendarBlockCard
                  lang={this.state.lang}
                  selectedDate={this.state.selectedDate}
                  daySelected={this.state.daySelected}
                  yearSelected={this.state.yearSelected}
                  onDateChange={this.setNewDate}
                  onModeChange={this.toggleMode}
                  currentTech={this.state.currentTech}
                  currentMode={this.state.currentMode}
                  calendarStats={this.state.data.calendarStats}
                  dataLoaded={this.state.dataLoaded}
                />
              </Grid>
            </Grid>
          </div>

          <Grid
            container
            spacing={this.state.windowWidth >= 600 ? 5 : 0}
            style={{ marginBottom: '80px' }}
          >
            <Grid item xs={4}>
              <Faq lang={this.state.lang} />
            </Grid>
            <Grid item xs={4}>
              <Card className={classes.downloadCard}>
                <button
                  className={classes.downloadCardButton}
                  onClick={this.toggleDownload}
                >
                  <CardHeader
                    className={`${classes.downloadCardHeader} bgdarkbluegreen`}
                    disableTypography
                    title="Download data"
                  />
                </button>
              </Card>
              <div className={classes.latestUpdateContainer}>
                {latestUpdate}
              </div>
            </Grid>
            <Grid item xs={4}>
              <Stats lang={this.state.lang} />
              <Copyright lang={this.state.lang} />
            </Grid>
          </Grid>
          {this.state.windowWidth < 768 && (
            <VeaBottomNavigation
              currentTech={this.state.currentTech}
              setEnergySource={this.setSelectedEnergySource}
              dataLoaded={this.state.dataLoaded}
              lang={this.state.lang}
            />
          )}
        </div>
        <DownloadDialog
          isOpen={this.state.isDownloadVisible}
          toggleClose={this.toggleDownload}
        ></DownloadDialog>
      </ThemeProvider>
    );
  }
}

App.propTypes = {
  classes: PropTypes.object.isRequired,
};
export default withMaintenanceMode(withStyles(styles)(App));
